import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  IconButton,
  Tooltip,
  Alert,
  CircularProgress,
  Switch,
  FormControlLabel,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Domain as DomainIcon,
  AdminPanelSettings as AdminIcon,
} from '@mui/icons-material';
import api from '../api/axios';

interface Tenant {
  id: number;
  name: string;
}

interface TenantDomain {
  id: number;
  tenant_id: number | null;
  domain: string;
  is_active: boolean;
  is_admin_panel: boolean;
  created_at: string;
  updated_at: string;
  tenant?: Tenant;
}

interface DomainsResponse {
  domains: TenantDomain[];
  total: number;
}

const DomainManagementPage: React.FC = () => {
  const [domains, setDomains] = useState<TenantDomain[]>([]);
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  // Dialog states
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingDomain, setEditingDomain] = useState<TenantDomain | null>(null);
  const [formData, setFormData] = useState({
    domain: '',
    tenant_id: '',
    is_active: true,
    is_admin_panel: false,
  });

  const fetchDomains = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const res = await api.get('/api/v1/admin/tenant-domains', {
        headers: { Authorization: `Bearer ${token}` },
      });
      setDomains((res.data as DomainsResponse).domains || []);
    } catch (err: any) {
      setError('Failed to fetch domains');
    } finally {
      setLoading(false);
    }
  };

  const fetchTenants = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await api.get('/api/v1/admin/tenants/', {
        headers: { Authorization: `Bearer ${token}` },
      });
      setTenants((res.data as Tenant[]) || []);
    } catch (err: any) {
      console.error('Failed to fetch tenants:', err);
    }
  };

  useEffect(() => {
    fetchDomains();
    fetchTenants();
  }, []);

  const handleCreateDomain = () => {
    setEditingDomain(null);
    setFormData({
      domain: '',
      tenant_id: '',
      is_active: true,
      is_admin_panel: false,
    });
    setDialogOpen(true);
  };

  const handleEditDomain = (domain: TenantDomain) => {
    setEditingDomain(domain);
    setFormData({
      domain: domain.domain,
      tenant_id: domain.tenant_id?.toString() || '',
      is_active: domain.is_active,
      is_admin_panel: domain.is_admin_panel,
    });
    setDialogOpen(true);
  };

  const handleDeleteDomain = async (domainId: number) => {
    if (!window.confirm('Are you sure you want to delete this domain?')) return;
    
    try {
      const token = localStorage.getItem('token');
      await api.delete(`/api/v1/admin/tenant-domains/${domainId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setSuccess('Domain deleted successfully');
      fetchDomains();
    } catch (err: any) {
      setError('Failed to delete domain');
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      const token = localStorage.getItem('token');
      const payload = {
        ...formData,
        tenant_id: formData.is_admin_panel ? null : parseInt(formData.tenant_id),
      };

      if (editingDomain) {
        await api.put(`/api/v1/admin/tenant-domains/${editingDomain.id}`, payload, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setSuccess('Domain updated successfully');
      } else {
        await api.post('/api/v1/admin/tenant-domains', payload, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setSuccess('Domain created successfully');
      }
      
      setDialogOpen(false);
      fetchDomains();
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to save domain');
    }
  };

  const getTenantName = (tenantId: number | null) => {
    if (!tenantId) return 'Admin Panel';
    const tenant = tenants.find(t => t.id === tenantId);
    return tenant ? tenant.name : `Tenant ${tenantId}`;
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" gutterBottom>
          Domain Management
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleCreateDomain}
        >
          Add Domain
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      <Paper>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Domain</TableCell>
                <TableCell>Tenant</TableCell>
                <TableCell>Type</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Created</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              ) : domains.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    <Typography color="text.secondary">
                      No domains found
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                domains.map((domain) => (
                  <TableRow key={domain.id}>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <DomainIcon color="primary" />
                        <Typography variant="body2" fontFamily="monospace">
                          {domain.domain}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {getTenantName(domain.tenant_id)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {domain.is_admin_panel ? (
                        <Chip
                          icon={<AdminIcon />}
                          label="Admin Panel"
                          color="secondary"
                          size="small"
                        />
                      ) : (
                        <Chip
                          label="Tenant Domain"
                          color="primary"
                          size="small"
                        />
                      )}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={domain.is_active ? 'Active' : 'Inactive'}
                        color={domain.is_active ? 'success' : 'default'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">
                        {new Date(domain.created_at).toLocaleDateString()}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Tooltip title="Edit Domain">
                        <IconButton
                          size="small"
                          onClick={() => handleEditDomain(domain)}
                        >
                          <EditIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete Domain">
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => handleDeleteDomain(domain.id)}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Add/Edit Domain Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <form onSubmit={handleSubmit}>
          <DialogTitle>
            {editingDomain ? 'Edit Domain' : 'Add New Domain'}
          </DialogTitle>
          <DialogContent>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
              <TextField
                label="Domain"
                value={formData.domain}
                onChange={(e) => setFormData({ ...formData, domain: e.target.value })}
                placeholder="https://example.com"
                fullWidth
                required
                helperText="Include protocol (https://) and full domain"
              />

              <FormControlLabel
                control={
                  <Switch
                    checked={formData.is_admin_panel}
                    onChange={(e) => setFormData({ 
                      ...formData, 
                      is_admin_panel: e.target.checked,
                      tenant_id: e.target.checked ? '' : formData.tenant_id
                    })}
                  />
                }
                label="Admin Panel Domain"
              />

              {!formData.is_admin_panel && (
                <FormControl fullWidth>
                  <InputLabel>Tenant</InputLabel>
                  <Select
                    value={formData.tenant_id}
                    onChange={(e) => setFormData({ ...formData, tenant_id: e.target.value })}
                    label="Tenant"
                    required
                  >
                    {tenants.map((tenant) => (
                      <MenuItem key={tenant.id} value={tenant.id.toString()}>
                        {tenant.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}

              <FormControlLabel
                control={
                  <Switch
                    checked={formData.is_active}
                    onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                  />
                }
                label="Active"
              />
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
            <Button type="submit" variant="contained">
              {editingDomain ? 'Update' : 'Create'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>
    </Box>
  );
};

export default DomainManagementPage;
